import React from 'react';
import { LucideIcon } from 'lucide-react';

interface ButtonProps {
  children: React.ReactNode;
  onClick?: (e?: React.MouseEvent<HTMLButtonElement | HTMLDivElement>) => void;
  variant?: 'primary' | 'outline' | 'success' | 'danger' | 'info' | 'neutral' | 'warning';
  icon?: LucideIcon;
  className?: string;
  iconClassName?: string;
  type?: 'button' | 'submit' | 'reset';
  disabled?: boolean;
  height?: string;
  padding?: string;
  fontSize?: string;
  fullWidth?: boolean;
  form?: string;
  title?: string;
  style?: React.CSSProperties;
}

export const Button: React.FC<ButtonProps> = ({
  children,
  onClick,
  variant = 'primary',
  icon: Icon,
  className = '',
  iconClassName: _iconClassName = '',
  type = 'button',
  disabled = false,
  height,
  padding,
  fontSize,
  fullWidth = false,
  form,
  title,
  style
}) => {
  const getVariantClasses = () => {
    const baseClasses = {
      primary: 'rounded-[8px] overflow-clip transition-all',
      outline: 'border-2 bg-white rounded-[8px] overflow-clip group hover:bg-[var(--color-primary)] transition-all',
      success: 'bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors',
      danger: 'bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors',
      info: 'bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors',
      neutral: 'bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors',
      warning: 'bg-amber-600 text-white rounded-md hover:bg-amber-700 transition-colors'
    };

    const cursorClass = disabled ? 'cursor-not-allowed' : 'cursor-pointer';
    const base = baseClasses[variant] || baseClasses.primary;
    return `${base} ${cursorClass}`;
  };

  const getTextClasses = () => {
    const baseClasses = {
      primary: 'capitalize font-medium leading-[26px] text-[17px] whitespace-pre transition-colors text-[var(--color-primary-text)]',
      outline: 'capitalize font-medium leading-[26px] text-[17px] whitespace-pre transition-colors group-hover:text-[var(--color-primary-text)]',
      success: 'font-medium text-white',
      danger: 'font-medium text-white',
      info: 'font-medium text-white',
      neutral: 'font-medium text-gray-700',
      warning: 'font-medium text-white'
    };

    const base = baseClasses[variant] || baseClasses.primary;
    return base;
  };

  const getIconClasses = () => {
    const baseClasses = {
      primary: 'w-4 h-4 transition-colors text-[var(--color-primary-text)]',
      outline: 'w-4 h-4 transition-colors group-hover:text-[var(--color-primary-text)]',
      success: 'w-4 h-4 text-white',
      danger: 'w-4 h-4 text-white',
      info: 'w-4 h-4 text-white',
      neutral: 'w-4 h-4 text-gray-700',
      warning: 'w-4 h-4 text-white'
    };

    const base = baseClasses[variant] || baseClasses.primary;
    return base;
  };

  // Build inline styles for theme-aware variants
  const getVariantStyles = (): React.CSSProperties => {
    const baseStyles: React.CSSProperties = style || {};

    if (variant === 'primary') {
      return {
        ...baseStyles,
        backgroundColor: 'var(--color-primary)',
        transition: 'all var(--transition-base)',
      };
    }

    if (variant === 'outline') {
      return {
        ...baseStyles,
        borderColor: 'var(--color-primary)',
        transition: 'all var(--transition-base)',
      };
    }

    return baseStyles;
  };

  // Get hover styles for primary button
  const getHoverStyles = (): React.CSSProperties | undefined => {
    if (disabled) return undefined;

    if (variant === 'primary') {
      return {
        backgroundColor: 'var(--color-primary-hover)',
        opacity: '0.9',
        transform: 'translateY(-1px)',
      };
    }

    return undefined;
  };

  // Simplified variants (success, danger, info, neutral, warning) use simpler styling
  const isSimpleVariant = ['success', 'danger', 'info', 'neutral', 'warning'].includes(variant);
  const defaultPadding = padding || (isSimpleVariant ? 'px-3 py-1.5' : 'px-[26px] py-[11px]');
  const defaultFontSize = fontSize || (isSimpleVariant ? 'text-base' : 'text-[17px]');
  const widthClass = fullWidth ? 'w-full' : '';

  // Detect if a custom colored background is applied via className (e.g., !bg-[var(--color-primary)])
  // Exclude white/transparent backgrounds and CSS variables
  const hasCustomBg = (className.includes('!bg-[') || className.includes('bg-[')) &&
                       !className.includes('bg-white') &&
                       !className.includes('!bg-white') &&
                       !className.includes('bg-transparent') &&
                       !className.includes('bg-[var(');

  return (
    <button
      type={type}
      form={form}
      className={`${getVariantClasses()} ${height || ''} ${widthClass} ${className} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      title={title}
      style={getVariantStyles()}
      onMouseEnter={(e) => {
        if (!disabled && getHoverStyles()) {
          Object.assign(e.currentTarget.style, getHoverStyles());
        }
      }}
      onMouseLeave={(e) => {
        if (!disabled) {
          Object.assign(e.currentTarget.style, getVariantStyles());
        }
      }}
    >
      <div className={`flex gap-[10px] items-center justify-center ${defaultPadding}`}>
        {Icon && (
          <div className="w-[16px] h-[16px] relative">
            <Icon
              className={`${getIconClasses()} ${
                variant === 'outline' ? 'text-[var(--color-primary)]' : ''
              }`}
              style={
                hasCustomBg
                  ? { color: '#ffffff' }
                  : undefined
              }
            />
          </div>
        )}
        <div
          className={`flex items-center gap-2 ${getTextClasses()} ${defaultFontSize} ${
            variant === 'outline' ? 'text-[var(--color-primary)]' : ''
          }`}
          style={
            hasCustomBg
              ? {
                  fontFamily: isSimpleVariant ? undefined : 'Roboto',
                  fontVariationSettings: isSimpleVariant ? undefined : "'wdth' 100",
                  color: '#ffffff',
                }
              : variant === 'primary' || variant === 'outline'
              ? {
                  fontFamily: 'Roboto',
                  fontVariationSettings: "'wdth' 100",
                }
              : isSimpleVariant
              ? undefined
              : { fontFamily: 'Roboto', fontVariationSettings: "'wdth' 100" }
          }
        >
          {children}
        </div>
      </div>
    </button>
  );
};

export default Button;